import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import Grid from '@mui/material/Grid';
import { keyframes } from '@mui/system';
import { ReactNode } from 'react';
import { colors } from '../../theme/theme';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
`;

interface StatCardProps {
  title: string;
  value: string | number;
  icon: ReactNode;
  color: string;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  isLoading?: boolean;
  delay?: number;
}

export const StatCard = ({
  title,
  value,
  icon,
  color,
  trend,
  isLoading = false,
  delay = 0,
}: StatCardProps) => {
  if (isLoading) {
    return (
      <Card
        sx={{
          height: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          border: `1px solid ${colors.grey[200]}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="80%" height={40} />
              <Skeleton variant="text" width="40%" height={16} sx={{ mt: 1 }} />
            </Box>
            <Skeleton variant="rectangular" width={56} height={56} sx={{ borderRadius: 2 }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        background: colors.gradients.card,
        border: `1px solid ${colors.grey[200]}`,
        transition: `all 200ms cubic-bezier(0.4, 0, 0.2, 1)`,
        animation: `${fadeInUp} 0.6s ease-out ${delay}ms both`,
        boxShadow: colors.shadows.sm,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: colors.shadows.md,
          borderColor: color,
          '& .stat-icon': {
            transform: 'scale(1.1) rotate(5deg)',
            boxShadow: `0 8px 24px ${color}60`,
          },
          '& .stat-icon-bg': {
            transform: 'scale(1.2)',
            opacity: 0.15,
          },
        },
      }}
    >
      {/* Decorative background element */}
      <Box
        className="stat-icon-bg"
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: 'none',
        }}
      />

      <CardContent sx={{ p: 3, position: 'relative' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          {/* Text Content */}
          <Box flex={1}>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.75rem',
                mb: 1.5,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: trend ? 1 : 0,
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>

            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: trend.isPositive ? 'success.bg' : 'error.bg',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: trend.isPositive ? 'success.dark' : 'error.dark',
                      fontSize: '0.75rem',
                    }}
                  >
                    {trend.isPositive ? '↑' : '↓'} {trend.value}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1, fontSize: '0.75rem' }}
                >
                  vs last month
                </Typography>
              </Box>
            )}
          </Box>

          {/* Icon */}
          <Box
            className="stat-icon"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: color,
              color: '#fff',
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: `0 4px 12px ${color}40`,
              '& svg': {
                fontSize: 28,
              },
            }}
          >
            {icon}
          </Box>
        </Box>

        {/* Progress bar (optional visual element) */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${color} 0%, transparent 100%)`,
            opacity: 0.3,
          }}
        />
      </CardContent>
    </Card>
  );
};

// Skeleton loader for multiple cards
export const StatCardSkeleton = ({ count = 4 }: { count?: number }) => {
  // Auto-calculate grid sizing based on count
  const getGridSize = () => {
    switch (count) {
      case 3:
        return { xs: 12, sm: 6, md: 4 };
      case 4:
        return { xs: 12, sm: 6, md: 6, lg: 3 };
      case 2:
        return { xs: 12, sm: 6 };
      default:
        return { xs: 12, sm: 6, md: 6, lg: 3 };
    }
  };

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <Grid key={index} size={getGridSize()}>
          <StatCard
            title=""
            value=""
            icon={<div />}
            color="#667eea"
            isLoading
          />
        </Grid>
      ))}
    </>
  );
};
